import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Download, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import { RiskBadge, severityFromScore } from "@/components/StatusBadge";
import VoiceInput from "@/components/VoiceInput";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/format";

const DOMAINS = [
  ["cx", "Customer & Front Office", "خدمة العملاء"], ["sales", "Sales & Marketing", "المبيعات والتسويق"],
  ["finance", "Finance", "المالية"], ["procurement", "Procurement & Supply Chain", "المشتريات"],
  ["hr", "Human Resources", "الموارد البشرية"], ["itops", "IT Operations", "عمليات تقنية المعلومات"],
  ["cyber", "Cybersecurity", "الأمن السيبراني"], ["grc", "Governance & Compliance", "الحوكمة والامتثال"],
  ["ops", "Operations", "العمليات"], ["transform", "Enterprise Transformation", "التحول المؤسسي"],
];
const STATUSES = ["Ideas", "Assessment", "Design", "Build", "Testing", "Pilot", "Production", "Absorption", "Adoption", "Optimisation", "Scaled", "Retired"];
const RISKS = ["Low", "Medium", "High", "Critical"];
const LAYER_KEYS = ["model", "system", "operation", "absorption", "outcome"];
const LAYER_COLORS = { optimal: "bg-emerald-500", warning: "bg-amber-500", critical: "bg-rose-500" };

export default function UseCases() {
  const { t, lang } = useLang();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [domain, setDomain] = useState("all");
  const [status, setStatus] = useState("all");
  const [risk, setRisk] = useState("all");
  const [readiness, setReadiness] = useState("all");

  const load = useMemo(() => async () => {
    const { data } = await api.get("/usecases", { params: { domain, status, risk, readiness, search } });
    setRows(data);
  }, [domain, status, risk, readiness, search]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!isAdmin) { toast.error(t("admin_only")); return; }
    const header = ["ID", "Name", "Domain", "Owner", "Status", "Risk", "Readiness", "ROI", "Value"];
    const lines = rows.map((c) => [c.uid, c.name, c.domain, c.businessOwner, c.lifecycleStatus, c.riskLevel, c.overallReadiness, c.roi, c.businessValue].join(","));
    const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "use-cases.csv"; a.click();
    toast.success(t("export"));
  };

  const getContext = () => ({ total: rows.length, filters: { domain, status, risk, readiness },
    sampleNames: rows.slice(0, 5).map((r) => r.name), avgReadiness: rows.length ? Math.round(rows.reduce((a, r) => a + r.overallReadiness, 0) / rows.length) : 0 });

  return (
    <div className="space-y-6 fade-up" data-testid="usecases-page">
      <PageHeader title={t("use_cases")} subtitle={`${rows.length} / 100`} screen="use-cases" getContext={getContext}>
        <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn" className="rounded-xl gap-2" disabled={!isAdmin}>
          <Download className="h-4 w-4" /> <span className="hidden sm:inline">{t("export")}</span>
        </Button>
      </PageHeader>

      <Card className="p-4 rounded-2xl shadow-sm space-y-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="usecase-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("search")} className="h-11 ps-9 rounded-xl" />
          </div>
          <VoiceInput onResult={(txt) => setSearch(txt)} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <FilterSelect testId="filter-domain" value={domain} onChange={setDomain} placeholder={t("domain")}
            options={[["all", t("all")], ...DOMAINS.map((d) => [d[0], lang === "ar" ? d[2] : d[1]])]} />
          <FilterSelect testId="filter-status" value={status} onChange={setStatus} placeholder={t("status")}
            options={[["all", t("all")], ...STATUSES.map((s) => [s, s])]} />
          <FilterSelect testId="filter-risk" value={risk} onChange={setRisk} placeholder={t("risk")}
            options={[["all", t("all")], ...RISKS.map((r) => [r, r])]} />
          <FilterSelect testId="filter-readiness" value={readiness} onChange={setReadiness} placeholder={t("readiness")}
            options={[["all", t("all")], ["high", "≥ 75%"], ["medium", "50–74%"], ["low", "< 50%"]]} />
        </div>
      </Card>

      <Card className="rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-start font-semibold px-4 py-3">ID</th>
                <th className="text-start font-semibold px-4 py-3">{t("name")}</th>
                <th className="text-start font-semibold px-4 py-3 hidden md:table-cell">{t("owner")}</th>
                <th className="text-start font-semibold px-4 py-3 hidden lg:table-cell">{t("layer_readiness")}</th>
                <th className="text-start font-semibold px-4 py-3">{t("risk")}</th>
                <th className="text-end font-semibold px-4 py-3 hidden sm:table-cell">{t("value")}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.uid} data-testid={`usecase-row-${c.uid}`}
                  onClick={() => navigate(`/use-cases/${c.uid}`)}
                  className="border-t hover:bg-secondary/40 cursor-pointer transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{c.uid}</td>
                  <td className="px-4 py-3 font-medium max-w-[220px] truncate">{lang === "ar" ? c.name_ar : c.name}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{c.businessOwner}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="flex items-center gap-1">
                      {LAYER_KEYS.map((k) => (
                        <div key={k} title={`${t(k)} ${c.scores[k]}%`} className="h-6 w-3 rounded-full bg-secondary overflow-hidden flex items-end">
                          <div className={LAYER_COLORS[severityFromScore(c.scores[k])]} style={{ height: `${c.scores[k]}%`, width: "100%" }} />
                        </div>
                      ))}
                      <span className="ms-2 text-xs font-semibold">{c.overallReadiness}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><RiskBadge risk={c.riskLevel} /></td>
                  <td className="px-4 py-3 text-end font-semibold hidden sm:table-cell">{fmtMoney(c.businessValue)}</td>
                  <td className="px-4 py-3"><ChevronRight className="h-4 w-4 text-muted-foreground rtl:rotate-180" /></td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">No use cases match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-10 rounded-xl" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
